import React, { useCallback, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { Link, Icon } from "components/common";
import { Row } from "components/bootstrap";
import ViewPropertiesModal from "views/components/dashboard/DashboardPropertiesModal";
import onSaveView from "views/logic/views/OnSaveViewAction";
import View from "views/logic/views/View";
import Routes from "routing/Routes";
import useViewTitle from "views/hooks/useViewTitle";
import useView from "views/hooks/useView";
import useViewsDispatch from "views/stores/useViewsDispatch";
import FavoriteIcon from "views/components/FavoriteIcon";
import { updateView } from "views/logic/slices/viewSlice";
import useIsNew from "views/hooks/useIsNew";
import { createGRN } from "logic/permissions/GRN";
import useAlertAndEventDefinitionData from "components/event-definitions/replay-search/hooks/useAlertAndEventDefinitionData";
import useReplaySearchContext from "components/event-definitions/replay-search/hooks/useReplaySearchContext";
import ViewsExecutionInfo from "views/components/views/ViewsExecutionInto";
const links = {
  [View.Type.Dashboard]: ({ id, title }) => [
    {
      link: Routes.DASHBOARDS,
      label: "Dashboards"
    },
    {
      label: title || id,
      dataTestId: "view-title"
    }
  ],
  [View.Type.Search]: ({ id, title }) => [
    {
      link: Routes.SEARCH,
      label: "Search"
    },
    {
      label: title || id,
      dataTestId: "view-title"
    }
  ],
  alert: ({ id }) => [
    {
      link: Routes.ALERTS.LIST,
      label: "Alerts & Events"
    },
    {
      label: id,
      dataTestId: "alert-id-title"
    }
  ],
  eventDefinition: ({ id, title }) => [
    {
      link: Routes.ALERTS.DEFINITIONS.LIST,
      label: "Event definitions"
    },
    {
      link: Routes.ALERTS.DEFINITIONS.show(id),
      label: title || id,
      dataTestId: "event-definition-title"
    }
  ]
};
const Content = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    margin-bottom: ${theme.spacings.xs};
    gap: ${theme.spacings.sm};
    justify-content: space-between;
  `
);
const Breadcrumb = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: ${theme.spacings.xxs};
  `
);
const EditButton = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.gray[60]};
    font-size: ${theme.fonts.size.tiny};
    cursor: pointer;
  `
);
const TitleWrapper = styled.span`
  display: flex;
  gap: 4px;
  align-items: center;

  & ${EditButton} {
    display: none;
  }

  &:hover ${EditButton} {
    display: block;
  }
`;
const StyledIcon = styled(Icon)`
  font-size: 0.5rem;
`;
const CrumbLink = ({
  label,
  link,
  dataTestId = void 0
}) => link ? /* @__PURE__ */ React.createElement(Link, { target: "_blank", to: link, "data-testid": dataTestId }, label) : /* @__PURE__ */ React.createElement("span", { "data-testid": dataTestId }, label);
const ViewHeader = () => {
  var _a;
  const view = useView();
  const isNew = useIsNew();
  const isSavedView = (view == null ? void 0 : view.id) && (view == null ? void 0 : view.title) && !isNew;
  const [showMetadataEdit, setShowMetadataEdit] = useState(false);
  const toggleMetadataEdit = useCallback(() => setShowMetadataEdit((cur) => !cur), [setShowMetadataEdit]);
  const { alertId, definitionId, type } = useReplaySearchContext();
  const { definitionTitle } = useAlertAndEventDefinitionData(alertId, definitionId);
  const dispatch = useViewsDispatch();
  const _onSaveView = useCallback(
    async (updatedView) => {
      await dispatch(onSaveView(updatedView));
      await dispatch(updateView(updatedView));
    },
    [dispatch]
  );
  const typeText = (_a = view == null ? void 0 : view.type) == null ? void 0 : _a.toLocaleLowerCase();
  const title = useViewTitle();
  const onChangeFavorite = useCallback(
    (newValue) => dispatch(updateView(view.toBuilder().favorite(newValue).build())),
    [dispatch, view]
  );
  const breadcrumbs = useMemo(() => {
    switch (type) {
      case "alert":
      case "event":
        return links.alert({ id: alertId });
      case "event_definition":
        return links.eventDefinition({ id: definitionId, title: definitionTitle });
      default:
        return links[view.type]({ id: view.id, title });
    }
  }, [type, alertId, definitionId, definitionTitle, view.type, view.id, title]);
  const showExecutionInfo = view.type === "SEARCH";
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Content, null, /* @__PURE__ */ React.createElement(Breadcrumb, null, breadcrumbs.map(({ label, link, dataTestId }, index) => {
    const theLast = index === breadcrumbs.length - 1;
    return /* @__PURE__ */ React.createElement(TitleWrapper, { key: `${label}_${link}` }, /* @__PURE__ */ React.createElement(CrumbLink, { link, label, dataTestId }), !theLast && /* @__PURE__ */ React.createElement(StyledIcon, { name: "chevron_right" }), isSavedView && theLast && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
      FavoriteIcon,
      {
        isFavorite: view.favorite,
        grn: createGRN(view.type, view.id),
        onChange: onChangeFavorite
      }
    ), /* @__PURE__ */ React.createElement(
      EditButton,
      {
        onClick: toggleMetadataEdit,
        role: "button",
        title: `Edit ${typeText} ${view.title} metadata`,
        tabIndex: 0
      },
      /* @__PURE__ */ React.createElement(Icon, { name: "edit_square" })
    )));
  }), showMetadataEdit && /* @__PURE__ */ React.createElement(
    ViewPropertiesModal,
    {
      show: true,
      view,
      title: `Editing saved ${typeText}`,
      onClose: toggleMetadataEdit,
      onSave: _onSaveView,
      submitButtonText: `Save ${typeText}`
    }
  )), showExecutionInfo && /* @__PURE__ */ React.createElement(ViewsExecutionInfo, null)));
};
export default ViewHeader;
